import { ChangeType, ViewChange, ViewChangeWord, SelectorChange } from './DiffReport';
import * as monaco from "monaco-editor/esm/vs/editor/editor.api";
import { MonacoStyles } from './styling/MonacoStyles';
import { Theme } from './DeltaResult';


interface DeltaObject {
  line: number,
  character: number,
  changeType: ChangeType
}

enum DeltaObjectTracking {
  None,
  InsertedObject,
  RemovedObject,
  ModifiedObject,
  RemovedValueTemp,
  InsertedValue,
  RemovedValue,
  ModifiedValue
}

enum DeltaType {
  Edit1,
  Edit2,
  A,
  B,
  None
}

export class RawDeltaJSON {
  public static getDecorations(monacoTheme: Theme,code: string) {
    const lines = code.split('\n');
    const lineCount = lines.length;
    const deltaObjects: DeltaObject[] = [];
    const selectorChangeLines: SelectorChange[] = [];
    let objChangeType = DeltaObjectTracking.None;
    let deltaNesting = -1;
    let deltaStringStart = -1;
    let isModifiedObject = false;


    const deltaDecorations: monaco.editor.IModelDeltaDecoration[] = [];
    for (let lineIndex = 0; lineIndex < lineCount; lineIndex++) {
      const fullLine = lines[lineIndex];
      const line = fullLine.trim();
      const isWithinDelta = deltaNesting > 0;

      if (!isWithinDelta) {
        if (line.startsWith('"dx_delta":')) {
          isModifiedObject = false;
          deltaNesting = 1;
          const charPos = fullLine.indexOf('"');
          selectorChangeLines.push({line: lineIndex, changeType: ChangeType.None, rawLine: -1});
          deltaObjects.push({ line: lineIndex, character: charPos, changeType: ChangeType.None });
        } else if (deltaStringStart === -1) {
          if (line.startsWith('"dx_delta_string')) {
            deltaStringStart = lineIndex;
          }
        } else {
          let char = line.charAt(0);
          if (char === ']') {
            deltaStringStart = -1;
          }
        }
      }
      else if (isWithinDelta) {
        let char = line.charAt(line.length - 1);
        char = char === ',' && line.length > 1 ? line.charAt(line.length - 2) : char;
        const deltaType = RawDeltaJSON.getDeltaType(line);
        switch (char) {
          case '[':
          case '{':
            deltaNesting++;
            if (objChangeType === DeltaObjectTracking.None) {
              if (deltaType === DeltaType.A || deltaType === DeltaType.Edit1) {
                objChangeType = DeltaObjectTracking.RemovedObject;
                const dObj3 = deltaObjects[deltaObjects.length - 1];
                dObj3.changeType = ChangeType.Removed;
                this.pushInlineDec(deltaType, monacoTheme, ChangeType.Removed, fullLine, lineIndex, deltaDecorations);
              } else if (deltaType === DeltaType.B || deltaType === DeltaType.Edit2) {
                objChangeType = DeltaObjectTracking.InsertedObject;
                const dObj3 = deltaObjects[deltaObjects.length - 1];
                dObj3.changeType = ChangeType.Inserted;
                this.pushInlineDec(deltaType, monacoTheme, ChangeType.Inserted, fullLine, lineIndex, deltaDecorations);
              }
            } 
            else if (objChangeType === DeltaObjectTracking.ModifiedObject) {
              this.pushInlineDec(deltaType, monacoTheme, ChangeType.Inserted, fullLine, lineIndex, deltaDecorations);
              // do not keep state as ModifiedObject - as this will cause subsequent lines to be highlighted also
              objChangeType = DeltaObjectTracking.InsertedObject;
            }
            break;
          case ']':
          case '}':
            deltaNesting--;
            objChangeType = DeltaObjectTracking.None;
            if (deltaNesting === 1) {
              const nextLine: string | null = lineIndex + 1 < lineCount? lines[lineIndex + 1] : null;
              if (nextLine) {
                if (RawDeltaJSON.isDeltaB(nextLine.trim())) {
                  objChangeType = DeltaObjectTracking.ModifiedObject;
                  isModifiedObject = true;
                }
              }
            } else if (deltaNesting === 0) {
              const poppedDeltaObj = deltaObjects.pop();
              if (deltaObjects.length === 0 && poppedDeltaObj) {
                // dectorate gutter now we have the end position
                // starts 1 before dx_delta starts and 1 after dx_delta ends
                RawDeltaJSON.pushRulerGutterMinimapDecorations(monacoTheme, poppedDeltaObj, lineIndex, deltaDecorations, isModifiedObject);
              }
            }
            break;
          default:
            if (objChangeType === DeltaObjectTracking.RemovedValueTemp) {
              if (deltaType === DeltaType.B || deltaType === DeltaType.Edit2) {
                const dObj3 = deltaObjects[deltaObjects.length - 1];
                dObj3.changeType = ChangeType.Modified;

                objChangeType = DeltaObjectTracking.ModifiedValue;
                this.pushInlineDec(deltaType, monacoTheme, ChangeType.Inserted, fullLine, lineIndex, deltaDecorations);
              } else {
                objChangeType = DeltaObjectTracking.RemovedValue;
              }
            } else if (objChangeType === DeltaObjectTracking.None) {
              if (deltaType === DeltaType.A || deltaType === DeltaType.Edit1) {
                const dObj3 = deltaObjects[deltaObjects.length - 1];
                dObj3.changeType = ChangeType.Removed;
                objChangeType = DeltaObjectTracking.RemovedValueTemp;
                this.pushInlineDec(deltaType, monacoTheme, ChangeType.Removed, fullLine, lineIndex, deltaDecorations);
                const nextLine: string | null = lineIndex + 1 < lineCount? lines[lineIndex + 1] : null;
                if (nextLine) {
                  if (RawDeltaJSON.isDeltaB(nextLine.trim())) {
                    objChangeType = DeltaObjectTracking.ModifiedObject;
                    isModifiedObject = true;
                  }
                }
              } else if (deltaType === DeltaType.B || deltaType === DeltaType.Edit2) {
                const dObj3 = deltaObjects[deltaObjects.length - 1];
                dObj3.changeType = ChangeType.Inserted;

                objChangeType = DeltaObjectTracking.InsertedValue;
                this.pushInlineDec(deltaType, monacoTheme, ChangeType.Inserted, fullLine, lineIndex, deltaDecorations);
              }
            } else if (objChangeType === DeltaObjectTracking.ModifiedObject) {              
              this.pushInlineDec(deltaType, monacoTheme, ChangeType.Inserted, fullLine, lineIndex, deltaDecorations);
            }

        }
      }
    }
    return {deltaDecorations, selectorChangeLines};
  }

  private static getDeltaType(text: string) {
    let result = DeltaType.None;
    if (text.startsWith('"A"')) {
      result = DeltaType.A;
    } else if (text.startsWith('"B"')) {
      result = DeltaType.B;
    } else if (text.startsWith('"edit1"')) {
      result = DeltaType.Edit1;
    } else if (text.startsWith('"edit2"')) {
      result = DeltaType.Edit2;
    }
    return result;
  }

  private static isDeltaB(text: string) {
    return text.startsWith('"B"') || text.startsWith('"edit2');
  }

  private static pushRulerGutterMinimapDecorations(monacoTheme: Theme, poppedDeltaObj: DeltaObject, lineIndex: number, deltaDecorations: monaco.editor.IModelDeltaDecoration[], isModifiedObject: boolean) {
    const overridenChangeType = isModifiedObject? ChangeType.Modified : poppedDeltaObj.changeType;
    const className = MonacoStyles.getGutterStyleName(monacoTheme, overridenChangeType);
    const isDarkTheme = monacoTheme === Theme.VSDark;
    let miniColor: string;
    if (overridenChangeType === ChangeType.Modified) {
      // purple
      miniColor = isDarkTheme? '#6e2f9975' : '#6e2f9975';
    } else if (poppedDeltaObj.changeType === ChangeType.Inserted) {
      // green
      miniColor = isDarkTheme? '#31958A45' : '#31958A45';
    } else {
      // red
      miniColor = isDarkTheme? '#892F4645' : '#892F4645';
    }
    const deltaDec: monaco.editor.IModelDeltaDecoration = {
      range: new monaco.Range(poppedDeltaObj.line, 1, lineIndex + 2, 1),
      options: {
        isWholeLine: true,
        linesDecorationsClassName: className,
        minimap: { position: monaco.editor.MinimapPosition.Inline, color: miniColor },
        overviewRuler: { position: monaco.editor.OverviewRulerLane.Full, color: miniColor }
      }
    };
    deltaDecorations.push(deltaDec);
  }

  private static pushInlineDec(deltaType: DeltaType, monacoTheme: Theme, type: ChangeType, fullLine: string, lineIndex: number, deltaDecorations: monaco.editor.IModelDeltaDecoration[]) {
    const className = MonacoStyles.getInlineStyleName(monacoTheme, type);
    const charPosInit = fullLine.indexOf('"');
    const charPos = fullLine.indexOf(':');
    const hasComma = fullLine.charAt(fullLine.length - 1) === ',';
    const endCharPos = hasComma ? fullLine.length : fullLine.length + 1;
    const deltaDec: monaco.editor.IModelDeltaDecoration = {
      range: new monaco.Range(lineIndex + 1, charPos + 3, lineIndex + 1, endCharPos),
      options: { inlineClassName: className }
    };
    //deltaDecorations.push(deltaDec);

    const wordEndPos = deltaType === DeltaType.Edit1 || deltaType === DeltaType.Edit2? charPosInit + 8 : charPosInit + 4;

    const deltaDec2: monaco.editor.IModelDeltaDecoration = {
      range: new monaco.Range(lineIndex + 1, charPosInit + 1, lineIndex + 1, wordEndPos),
      options: { inlineClassName: className }
    };
    //deltaDecorations.push(deltaDec2);

  }

  public static pushDiffViewLineDec(monacoTheme: Theme, viewChange: ViewChange, deltaDecorations: monaco.editor.IModelDeltaDecoration[]) {
    const className = MonacoStyles.getLineStyleName(monacoTheme, viewChange.changeType);
    const startLine = viewChange.line;
    const endLine = viewChange.endLine? viewChange.endLine : startLine;
    const miniColor = viewChange.changeType === ChangeType.Inserted ? '#9bb95535' : '#ff000055';
    const deltaDec: monaco.editor.IModelDeltaDecoration = {
      range: new monaco.Range(startLine, 1, endLine, 1),
      options: {
        className: className,
        isWholeLine: true,
        minimap: { position: monaco.editor.MinimapPosition.Inline, color: miniColor },
        overviewRuler: { position: monaco.editor.OverviewRulerLane.Full, color: miniColor}
      }
    };
    deltaDecorations.push(deltaDec);
    if (viewChange.isEmphasized) {
      deltaDecorations.push(deltaDec);
    }
  }

  public pushDiffViewWordDec(monacoTheme: Theme, viewChange: ViewChangeWord, deltaDecorations: monaco.editor.IModelDeltaDecoration[]) {
    const className = MonacoStyles.getInlineStyleName(monacoTheme, viewChange.changeType);
    const line = viewChange.line;

    const deltaDec: monaco.editor.IModelDeltaDecoration = {
      range: new monaco.Range(line, viewChange.startChar, line, viewChange.endChar),
      options: { inlineClassName: className }
    };
    deltaDecorations.push(deltaDec);
  }

}